import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { EmailDomainValidator } from '@serasa/shared/validators/email.validator';
import { Observable, Subscription } from 'rxjs';

import { SiteSection } from '../../../../../shared/enums/site-section.enum';
import { AnalyticsService } from '../../../../../shared/services/analytics.service';
import { SidenavManagerService } from '../../../../../shared/services/sidenav.manager.service';
import { Utils } from '../../../../../shared/utils/utils';
import { ProfileFilterEnum } from '../../../dashboard/components/search-form/components/filters/enums';
import { Logon } from '../../interfaces/logon.interface';
import { IUserAccountUpdatePayload } from '../../interfaces/payload-update-user-account.interface';
import { UserEditForm } from '../../interfaces/user-edit-form.enum';
import { UserState } from '../../interfaces/user-state.interface';
import {
  closeSideNav,
  updateSelectedLogon,
} from '../../state/actions/user.actions';
import {
  selectErrorUpdateUserAccount,
  selectLogon,
  selectUpdateUserAccount,
} from '../../state/selectors/user.selectors';
import { UserEditMessagesHelper } from './user-edit-messages.helper';

type ProfileObjType = { key: string; value: string };
type StoredLogonType = Partial<Logon | undefined>;
type HttpErrorType = HttpErrorResponse | null | undefined;

interface IUserEditMessagesHelper {
  title: string;
  description: string;
  isEmailError: boolean;
}

@Component({
    selector: 'pl-user-edit',
    templateUrl: './user-edit.component.html',
    styleUrls: ['./user-edit.component.scss'],
    standalone: false
})
export class UserEditComponent implements OnInit, OnDestroy {
  public userEditForm!: UntypedFormGroup;
  public selectedValue = 'usuario';
  public hasDocument = true;
  public currentUser: any = {};
  public messageErrorObject: IUserEditMessagesHelper = {
    description: '',
    isEmailError: false,
    title: '',
  };

  public selectedLogon$: Observable<Partial<Logon | undefined>>;
  public error$!: Observable<HttpErrorType>;
  public updateAccount$: Observable<UserState['updateUserAccount']>;
  public accountError$!: Observable<HttpErrorType>;

  public notificationsOptions = {
    status: 'warning',
    title: 'default',
    message: 'default',
  };

  public statusBarOptions = {
    status: 'warning',
    message: 'default',
  };

  public profileList = [
    {
      key: '0',
      value: ProfileFilterEnum.USER,
    },
    {
      key: '1',
      value: ProfileFilterEnum.ADMIN,
    },
    {
      key: '2',
      value: ProfileFilterEnum.MANAGER,
    },
  ];

  public userProfile = '0';

  private readonly _subs: Subscription[] = [];

  constructor(
    private readonly store: Store,
    private readonly _sidenavManagerService: SidenavManagerService,
    private readonly _analyticsService: AnalyticsService,
    private readonly _fb: UntypedFormBuilder,
    private readonly _cdr: ChangeDetectorRef
  ) {
    this.selectedLogon$ = this.store.select(selectLogon);
    this.updateAccount$ = this.store.select(selectUpdateUserAccount);
    this.error$ = this.store.select(selectErrorUpdateUserAccount);
  }

  ngOnInit() {
    this._verifyHttpError();
    this._verifyCurrentLogon();

    this._analyticsService.doPageLoad(SiteSection.EDITAR_USUARIO);
  }

  public closeSideNav(): void {
    this.store.dispatch(closeSideNav());
  }

  public save(): void {
    const logon: string = this.currentUser?.logon;
    const userId: string = this.currentUser?.userId;
    const payload = this._createPayloadForUserAccessUpdate();

    this.store.dispatch(
      updateSelectedLogon({
        userId,
        logon,
        payload,
      })
    );

    this._subs.push(
      this.updateAccount$.subscribe((updateStatus) => {
        if (!updateStatus?.error && !updateStatus?.isLoading) {
          this._sidenavManagerService.navigate$.next('confirmation');
        }
      })
    );
  }

  private _createPayloadForUserAccessUpdate(): IUserAccountUpdatePayload {
    const userFormValues: UserEditForm = this.userEditForm.value;
    const phoneObject = this._createPhoneObject(userFormValues);
    const documentObject = this._createDocumentObject(userFormValues);

    return {
      fullName: userFormValues?.name || '',
      email: userFormValues?.email || '',
      ...documentObject,
      ...phoneObject,
    } as IUserAccountUpdatePayload;
  }

  private _createUserEditForm(): void {
    const { name, email, profile } = this.currentUser || {};
    const { documentId, documentType } = this.currentUser?.document ?? {
      documentId: '',
      documentType: 'CPF',
    };
    const { areaCode, phoneNumber } = this.currentUser?.phone ?? {
      areaCode: '',
      regionCode: '',
      phoneNumber: '',
    };

    this.hasDocument = this.currentUser?.document;

    const selectedProfile = this.profileList.find(
      (profileObject: ProfileObjType) => {
        type ProfileFilterEnumKeys = keyof typeof ProfileFilterEnum;
        return (
          profileObject.value ===
          ProfileFilterEnum[profile as ProfileFilterEnumKeys]
        );
      }
    );

    this.userProfile = selectedProfile?.key || '0';

    this.userEditForm = this._fb.group({
      name: new UntypedFormControl(name),
      profile: new UntypedFormControl({ value: `${this.userProfile}` }),
      document: new UntypedFormControl({
        value: documentId,
        disabled: this.hasDocument,
      }, Validators.compose([Validators.minLength(11)])),
      isForeign: new UntypedFormControl(documentType !== 'CPF'),
      email: [email,[new EmailDomainValidator(Utils.emailRegex()).validate()]],
      mobile: new UntypedFormControl(`${areaCode}${phoneNumber}`),
    });

    this._subs.push(
      this.userEditForm.controls['isForeign'].valueChanges.subscribe(
        (isForeign: boolean) => {
          this.userEditForm.controls['document'].setValue('');
          if (isForeign) {
            this.userEditForm.controls['document'].clearValidators();
          } else {
            this.userEditForm.controls['document'].setValidators(Validators.minLength(11));
          }
          this.userEditForm.markAllAsTouched();
        }
      )
    );

    this._cdr.detectChanges();
  }

  private _createPhoneObject(userFormValues: UserEditForm) {
    const phone = userFormValues?.mobile.trim() || '';

    if (phone) {
      return {
        phone: {
          ...Utils.transformPhoneToObject(phone),
        },
      };
    }

    return {};
  }

  private _createDocumentObject(userFormValues: UserEditForm) {
    const document: string = this.userEditForm.controls['document'].value || '';

    if (document) {
      return {
        document: {
          documentCountry: userFormValues.isForeign ? 'RNE' : 'BRA',
          documentId: userFormValues.isForeign
            ? document
            : document.replace(/[^\d]/g, ''),
          documentType: userFormValues.isForeign ? 'RNE' : 'CPF',
        },
      };
    }

    return {};
  }

  private _verifyCurrentLogon() {
    this._subs.push(
      this.selectedLogon$.subscribe((storedLogon: StoredLogonType) => {
        if (!storedLogon?.error) {
          this.currentUser = storedLogon;
          this._createUserEditForm();
        } else {
          this.messageErrorObject = new UserEditMessagesHelper(
            storedLogon?.error
          ).error;
        }
      })
    );
  }

  private _verifyHttpError() {
    this._subs.push(
      this.error$.subscribe((error: HttpErrorType) => {
        if (error) {
          this.messageErrorObject = new UserEditMessagesHelper(error).error;
          this._setInvalidEmailError();
        }
      })
    );
  }

  private _setInvalidEmailError() {
    if (this.messageErrorObject.isEmailError) {
      this.userEditForm.controls['email'].setErrors({
        invalidEmail: 'Esse e-mail não pode ser utilizado.',
      });
    }
  }

  ngOnDestroy() {
    for (const sub of this._subs) {
      sub.unsubscribe();
    }
  }
}
